import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Building2, Pencil, Trash2, SearchX, Loader2, LayoutGrid, List } from 'lucide-react';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { usePermissions } from '../hooks/usePermissions';
import { CustomerPortalAccessFields } from '../components/contacts/ContactForm';
import { accessBlocker, customerPortalAccessPayload, followEmail } from '../components/contacts/contactAccessModel';
import { toast } from '../store/toastStore';
import { apiErrorMessage } from '../components/sales/shared';
import EmptyState from '../components/EmptyState';
import PageHeader from '../components/PageHeader';
import Badge from '../components/ui/Badge';
import FilterBar, { type FilterDef } from '../components/ui/FilterBar';
import { ListsSelect } from '../components/ui/ListsKit';
import DataTable, { type CardSpec, type Column, type RowAction } from '../components/DataTable';
import { useIsDesktop } from '../components/ui/useOverlay';
import {
  WorkTypeTags, EngagementFilterControl, EMPTY_ENGAGEMENT_FILTER, engagementFilterCount, type EngagementFilterValue,
} from '../components/CustomerHub/CustomerWorkTypes';
import { foldLegacyEngagementFlags } from '../components/CustomerHub/engagementFilterModel';
import { formatDate } from '../utils/format';
import type { ViewMode } from '../components/ViewToggle';
import type { Organization } from '../types';
import HardDeleteCustomerModal from '../components/CustomerHub/HardDeleteCustomerModal';
import Dialog from '../components/ui/Dialog';
import Field, { FormErrorSummary, FormServerError, rules, useFormValidation, type FormSchema } from '../components/ui/Field';
import { reportScheduledMail } from '../utils/scheduledMail';
import EntityPicker from '../components/ui/EntityPicker';
import DateInput from '../components/ui/DateInput';

/**
 * Sort keys the Customer List may order by. They are OPAQUE: each key is sent to
 * the server, which resolves it against its own allowlist before touching SQL.
 * The client never sends a column name, so this list is a UI vocabulary, not a
 * query fragment.
 */
const SORT_KEYS = ['name', 'joined_at', 'active', 'account_manager'] as const;
type SortKey = (typeof SORT_KEYS)[number];

const PAGE_SIZE = 25;

/** localStorage key for the table/cards preference — presentation only. */
const VIEW_STORAGE_KEY = 'ote-organizations-view';

function readStoredView(): ViewMode {
  try {
    const v = localStorage.getItem(VIEW_STORAGE_KEY);
    return v === 'cards' ? 'cards' : 'list';
  } catch {
    // Private mode / storage disabled: fall back to the table, never throw.
    return 'list';
  }
}

/**
 * `joined_at` is a DATE column with no time part. Parsing `'2024-03-01'` with
 * `new Date()` reads it as UTC midnight, which renders as the PREVIOUS day for
 * any viewer west of UTC — so the date is formatted from its parts instead.
 */
function formatJoinedAt(value?: string | null): string {
  if (!value) return '—';
  const iso = String(value).slice(0, 10);
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '—';
  return formatDate(new Date(y, m - 1, d));
}

/** Quiet placeholder for an empty cell. */
function Dash() {
  return <span className="text-text-3" aria-hidden="true">—</span>;
}

type Customer = Organization & { account_manager_name?: string | null };

export default function OrganizationsPage() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin';
  const isAgent = user?.role === 'agent';
  const canManage = isAdmin || isAgent;
  const isDesktop = useIsDesktop();

  // P-06/G (D-58) — the search, filters, sort and page start from the URL and
  // are written back to it (replace), so "← חזרה" from a customer restores them.
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialUrl] = useState(() => readOrgListUrl(searchParams));
  const [search, setSearch] = useState(initialUrl.search);
  // ---- Filters. All four NARROW the list; none of them can widen it. The
  // server applies the caller's Customer scope first and ANDs these after it.
  // P-01 follow-up (D1) — the list opens on the active customers.
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>(initialUrl.status);
  const [accountManagerId, setAccountManagerId] = useState(initialUrl.accountManagerId);
  const [joinedFrom, setJoinedFrom] = useState(initialUrl.joinedFrom);
  const [joinedTo, setJoinedTo] = useState(initialUrl.joinedTo);
  // P-01/B3 — engagement filters (server-side, like the others).
  const [engagementFilter, setEngagementFilter] = useState<EngagementFilterValue>(initialUrl.engagementFilter);
  // ---- Sorting.
  const [sort, setSort] = useState<SortKey>(initialUrl.sort);
  const [dir, setDir] = useState<'asc' | 'desc'>(initialUrl.dir);
  // ---- Pagination.
  const [page, setPage] = useState(initialUrl.page);
  useEffect(() => {
    setSearchParams((prev) => writeOrgListUrl(prev, { search, status, accountManagerId, joinedFrom, joinedTo, engagementFilter, sort, dir, page }), { replace: true });
  }, [search, status, accountManagerId, joinedFrom, joinedTo, engagementFilter, sort, dir, page, setSearchParams]);
  // ---- View. Presentation only: it is deliberately NOT part of the query key
  // below, so switching table ↔ cards re-renders the SAME rows and cannot
  // invalidate or re-issue the request. Search, filters, sorting and page are
  // untouched by the switch.
  const [view, setView] = useState<ViewMode>(readStoredView);

  const [showCreateModal, setShowCreateModal] = useState(false);
  // Command palette quick action (u5): /organizations?create=true opens the form.
  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setShowCreateModal(true);
      const sp = new URLSearchParams(searchParams);
      sp.delete('create');
      setSearchParams(sp, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [editOrg, setEditOrg] = useState<Organization | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Organization | null>(null);

  const offset = page * PAGE_SIZE;
  // D-352: the OTE manager can bring the demo customers back into the list.
  const isGM = usePermissions().capabilities.isGeneralManager;
  const [showDemo, setShowDemo] = useState(false);

  const { data: orgsData, isLoading, isError, error: orgsError, refetch } = useQuery({
    queryKey: ['organizations', { search, status, accountManagerId, joinedFrom, joinedTo, engagementFilter, sort, dir, offset, showDemo: isGM && showDemo }],
    queryFn: () => api.getOrganizations({
      include_demo: isGM && showDemo ? 1 : undefined,
      search: search || undefined,
      status,
      account_manager_id: accountManagerId || undefined,
      joined_from: joinedFrom || undefined,
      joined_to: joinedTo || undefined,
      engagement_types: engagementFilter.types.length ? engagementFilter.types.join(',') : undefined,
      engagement_active_only: engagementFilter.types.length && engagementFilter.activeOnly ? true : undefined,
      sort,
      dir,
      limit: PAGE_SIZE,
      offset,
    }),
  });

  const changeView = (mode: ViewMode) => {
    setView(mode);
    try { localStorage.setItem(VIEW_STORAGE_KEY, mode); } catch { /* preference is best-effort */ }
  };

  /** Any change to what the user is looking at returns them to the first page. */
  const updateFilter = <T,>(setter: (v: T) => void) => (value: T) => {
    setter(value);
    setPage(0);
  };

  const hasActiveFilters =
    status !== 'active' || !!accountManagerId || !!joinedFrom || !!joinedTo || engagementFilterCount(engagementFilter) > 0;

  const clearFilters = () => {
    setStatus('active');
    setAccountManagerId('');
    setJoinedFrom('');
    setJoinedTo('');
    setEngagementFilter(EMPTY_ENGAGEMENT_FILTER);
    setPage(0);
  };

  // Account-manager candidates. `ote_staff` restricts to canonical active OTE
  // employees, which is exactly what the backend's
  // `assertAccountManagerCandidate` requires on save — without it, the picker
  // would offer `users.role = 'agent'` identities (e.g. legacy/customer-side
  // rows with no employee_profiles entry) that the server rejects.
  const { data: agentsData } = useQuery({
    // QA-R1 #6 — every active OTE employee, whatever their role (the server rule).
    queryKey: ['users', 'ote_staff', 'account-manager-candidates'],
    queryFn: () => api.getUsers({ ote_staff: true, active: true, limit: 200 }),
    enabled: canManage,
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      // Contacts are created only after the organization insert returns: a
      // contact carries `organization_id`, which does not exist until then.
      // D-138 / D-141: `portal_access` travels WITH the customer — the server
      // creates the portal contact, login and grant in the same transaction.
      const { _contacts = [], ...orgPayload } = data;
      const org = await api.createOrganization(orgPayload);
      const orgId = org?.data?.id;
      let failedContacts = 0;
      if (orgId && _contacts.length) {
        // A failing contact must not lose the organization that was already
        // created, so each one is isolated and counted rather than thrown.
        for (const c of _contacts as any[]) {
          try {
            await api.createContact({ ...c, organization_id: orgId });
          } catch {
            // The organization exists and is visible; the contact can be
            // re-added from the Contacts screen (reported in onSuccess).
            failedContacts += 1;
          }
        }
      }
      return { org, failedContacts };
    },
    onSuccess: ({ org, failedContacts }) => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setShowCreateModal(false);
      // D-331 QX-2: created with no Account Manager (a TL / PM) - say what happens next, instead of the customer just vanishing.
      if (org?.data && !org.data.account_manager_id) toast('success', t('admin.createdWaitingForAm'));
      if (failedContacts > 0) {
        toast('warning', t('organizations.contactsNotSaved', { count: failedContacts }));
      }
      if (reportScheduledMail(org)) { /* P-27: queued - "בטל" toast */ }
      else if (org?.email_sent === true) toast('success', t('portalAccess.emailSent'));
      else if (org?.email_sent === false) toast('warning', t('portalAccess.emailFailed'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.updateOrganization(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['organizations'] }); setShowCreateModal(false); setEditOrg(null); },
  });

  const organizations = (orgsData?.data ?? []) as Customer[];
  const agents = agentsData?.data ?? [];
  const total = orgsData?.meta?.count ?? organizations.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const sortLabel: Record<SortKey, string> = {
    name: t('organizations.sortName'),
    joined_at: t('organizations.sortJoinedAt'),
    active: t('organizations.sortStatus'),
    account_manager: t('organizations.sortAccountManager'),
  };

  /** Sort control for when the table headers are not on screen (cards view, phones). */
  const sortSelect = (
    <ListsSelect
      label={t('organizations.sortBy')}
      value={`${sort}:${dir}`}
      onChange={e => {
        const [k, d] = e.target.value.split(':') as [SortKey, 'asc' | 'desc'];
        setPage(0);
        setSort(k);
        setDir(d);
      }}
    >
      {SORT_KEYS.flatMap((k) => ([
        <option key={`${k}:asc`} value={`${k}:asc`}>{`${t('organizations.sortBy')}: ${sortLabel[k]} · ${t('organizations.sortAsc')}`}</option>,
        <option key={`${k}:desc`} value={`${k}:desc`}>{`${t('organizations.sortBy')}: ${sortLabel[k]} · ${t('organizations.sortDesc')}`}</option>,
      ]))}
    </ListsSelect>
  );

  const openCreate = () => { setEditOrg(null); setShowCreateModal(true); };
  // C-24 (clarity, D-04): an employee keeps the action, but not as the page's blue primary.
  const addButton = canManage ? (
    <button type="button" onClick={openCreate} className={isAgent ? 'btn btn-secondary' : 'btn btn-primary'}>
      <Plus className="w-4 h-4" aria-hidden="true" /> {t('admin.newOrg')}
    </button>
  ) : undefined;

  // P-01/B5 — table / cards as two visible buttons beside "לקוח חדש"; the
  // chosen one is pressed. Presentation only (stored in localStorage).
  const viewButton = (mode: ViewMode, icon: ReactNode, label: string) => (
    <button
      type="button"
      onClick={() => changeView(mode)}
      aria-pressed={view === mode}
      className={`btn btn-sm ${view === mode ? 'btn-secondary border-accent text-accent-text' : 'btn-ghost text-text-2'}`}
    >
      {icon} {label}
    </button>
  );
  const headerActions = (
    <div className="flex flex-wrap items-center gap-2">
      <div role="group" aria-label={t('organizations.viewModeLabel')} className="flex items-center gap-1">
        {viewButton('cards', <LayoutGrid size={16} strokeWidth={1.75} aria-hidden="true" />, t('organizations.viewCardsShort'))}
        {viewButton('list', <List size={16} strokeWidth={1.75} aria-hidden="true" />, t('organizations.viewTableShort'))}
      </div>
      {isGM && (
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-text">
          <input type="checkbox" checked={showDemo} onChange={(e) => { setShowDemo(e.target.checked); setPage(0); }} className="h-4 w-4 rounded-xs accent-primary-700" />
          {t('organizations.showDemo')}
        </label>
      )}
      {addButton}
    </div>
  );

  /* ---------------- Filters (FilterBar v2) ----------------
     Every control NARROWS the list the server already authorized; none of
     them can widen it. Status, account manager and the joined-date range
     (on the canonical `joined_at`, not `created_at`) live behind "סינון". */
  const filters: FilterDef[] = [
    {
      key: 'status',
      label: t('organizations.filterStatus'),
      // The default "פעילים" is the filter's empty value, so it is not counted
      // as an active filter; "בארכיון" and "הכל" are.
      value: status === 'active' ? '' : status,
      options: [
        { value: 'inactive', label: t('organizations.statusFilterInactive') },
        { value: 'all', label: t('organizations.statusAll') },
      ],
      allLabel: t('organizations.statusFilterActive'),
      onChange: (v) => updateFilter(setStatus)((v || 'active') as 'all' | 'active' | 'inactive'),
    },
    // The same canonical OTE-staff list the create/edit form uses, so the
    // filter can only name a manager the server accepts.
    ...(canManage ? [{
      key: 'am',
      kind: 'search-select' as const,
      label: t('organizations.filterAccountManager'),
      value: accountManagerId,
      options: agents.map((agent: any) => ({ value: agent.id as string, label: agent.name as string })),
      allLabel: t('organizations.allAccountManagers'),
      onChange: (v: string) => updateFilter(setAccountManagerId)(v),
    }] : []),
    {
      key: 'joined',
      kind: 'dateRange',
      label: t('organizations.joinedAt'),
      from: joinedFrom,
      to: joinedTo,
      fromLabel: t('organizations.filterJoinedFrom'),
      toLabel: t('organizations.filterJoinedTo'),
      onRangeChange: (patch) => {
        if (patch.from !== undefined) setJoinedFrom(patch.from);
        if (patch.to !== undefined) setJoinedTo(patch.to);
        setPage(0);
      },
    },
  ];

  /* ---------------- Table (DataTable v2) ---------------- */
  const statusBadge = (org: Customer) => (
    <Badge tone={org.active ? 'success' : 'neutral'} dot>
      {org.active ? t('contacts.active') : t('contacts.inactive')}
    </Badge>
  );
  // P-01/B1 — name | status | account manager | phone | email | work types |
  // joined | actions (the row actions column comes last by itself).
  const columns: Column<Customer>[] = [
    {
      key: 'name',
      header: t('admin.orgName'),
      link: true,
      sortable: true,
      width: 'min-w-40',
      render: (org) => <bdi>{org.name}</bdi>,
    },
    { key: 'active', header: t('contacts.status'), sortable: true, width: 'w-28', render: statusBadge },
    {
      key: 'account_manager',
      header: t('admin.accountManager'),
      sortable: true,
      width: 'w-40',
      render: (org) => (org.account_manager_name ? <span className="block max-w-40 truncate text-text-2">{org.account_manager_name}</span> : <Dash />),
    },
    {
      key: 'phone',
      header: t('admin.orgPhone'),
      priority: 3,
      width: 'w-32',
      render: (org) => (org.phone ? <span dir="ltr" className="whitespace-nowrap tabular-nums text-text-2">{org.phone}</span> : <Dash />),
    },
    {
      key: 'email',
      header: t('organizations.email'),
      // D-284 (was FQ-049 'hidden 2xl:table-cell'): no column hides by width; the table scrolls inside its frame instead.
      width: 'w-56',
      render: (org) => (org.email ? <span dir="ltr" className="block max-w-56 truncate text-text-2">{org.email}</span> : <Dash />),
    },
    {
      key: 'work_types',
      header: t('organizations.workTypes'),
      width: 'min-w-40',
      render: (org) => <WorkTypeTags workTypes={org.work_types} inCharacterization={org.in_characterization} />,
    },
    {
      key: 'joined_at',
      header: t('organizations.joinedAt'),
      align: 'end',
      sortable: true,
      priority: 2,
      width: 'w-32',
      render: (org) => <span className="whitespace-nowrap text-text-2">{formatJoinedAt(org.joined_at)}</span>,
    },
  ];

  // P-01/B5 — a card: name, status, account manager, phone, email and the
  // work-type tags; the card opens the customer page.
  // QA-R1 #9 — the tags are visible on the card itself (next to the status),
  // never folded under "more details".
  const card = (org: Customer): CardSpec => ({
    title: <bdi>{org.name}</bdi>,
    badges: (
      <>
        {statusBadge(org)}
        {(org.work_types?.length ?? 0) > 0 && <WorkTypeTags workTypes={org.work_types} />}
      </>
    ),
    metaItems: [
      org.account_manager_name,
      org.phone ? <span dir="ltr" className="tabular-nums">{org.phone}</span> : null,
      org.email ? <span dir="ltr">{org.email}</span> : null,
    ],
  });

  // D5: edit shows on hover / focus; delete (admin) lives in ⋯ (on touch: all in ⋯).
  const rowActions = (org: Customer): RowAction[] => {
    const actions: RowAction[] = [
      { key: 'edit', label: t('common.edit'), icon: <Pencil size={16} strokeWidth={1.75} />, inline: true, onSelect: () => { setEditOrg(org); setShowCreateModal(true); } },
    ];
    if (isAdmin) {
      actions.push({ key: 'delete', label: t('common.delete'), icon: <Trash2 size={16} strokeWidth={1.75} />, danger: true, separatorBefore: true, onSelect: () => setDeleteTarget(org) });
    }
    return actions;
  };

  return (
    <div>
      <PageHeader title={t('admin.organizations')} actions={headerActions} />
      {/* D-321 - a fixed, separate entry to the company's own work (not a customer: not counted, not filtered). */}
      <Link
        to="/internal-work"
        data-testid="internal-hub-link"
        className="mb-3 inline-flex items-center gap-2 rounded-lg border border-surface-200 bg-surface-50 px-3 py-2 text-sm font-medium text-surface-800 hover:bg-surface-100"
      >
        <Building2 size={16} aria-hidden="true" /> {t('internalHub.link')}
      </Link>

      <FilterBar
        className="mb-4"
        search={search}
        onSearchChange={updateFilter(setSearch)}
        searchPlaceholder={t('organizations.search')}
        filters={filters}
        onClearAll={() => { setSearch(''); clearFilters(); }}
        onClearFilters={clearFilters}
        summary={!isLoading && !isError ? t('organizations.resultsCount', { count: total }) : null}
      >
        <EngagementFilterControl value={engagementFilter} onChange={updateFilter(setEngagementFilter)} />
        {(view === 'cards' || !isDesktop) && sortSelect}
      </FilterBar>

      {/* Table and cards render the SAME `organizations` array from the same
          request, filters and sort, so the two views never disagree. */}
      <DataTable
        columns={columns}
        data={organizations}
        keyField="id"
        caption={t('admin.organizations')}
        mobile="cards"
        layout={view === 'cards' ? 'cards' : 'table'}
        getRowHref={(org) => `/organizations/${org.id}`}
        rowLabel={(org) => org.name}
        card={card}
        rowActions={canManage ? rowActions : undefined}
        sort={{ key: sort, dir }}
        onSortChange={(next) => {
          setPage(0);
          setSort((next?.key as SortKey) ?? 'name');
          setDir(next?.dir ?? 'asc');
        }}
        isLoading={isLoading}
        skeletonRows={10}
        error={isError ? orgsError ?? true : undefined}
        errorMessage={isError ? apiErrorMessage(orgsError, t('common.loadFailed')) : undefined}
        onRetry={() => { void refetch(); }}
        emptyState={hasActiveFilters || search ? (
          <EmptyState
            icon={SearchX}
            title={t('uxLists.noMatchesTitle')}
            description={t('uxLists.noMatchesDesc')}
            action={hasActiveFilters ? (
              <button type="button" onClick={clearFilters} className="btn btn-secondary">
                {t('organizations.clearFilters')}
              </button>
            ) : undefined}
          />
        ) : (
          // C-24 (clarity, D-04): an employee is told where their customers show up; no "הוסיפו לקוח" and no primary here.
          <EmptyState icon={Building2} title={t('admin.noOrgs')} description={isAgent ? t('organizations.emptyAgentHint') : t('uxLists.orgsEmptyDesc')} action={isAgent ? undefined : addButton} />
        )}
        pagination={pageCount > 1 ? {
          page: page + 1,
          pageCount,
          total,
          onChange: (p) => setPage(Math.min(pageCount - 1, Math.max(0, p - 1))),
        } : undefined}
      />

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <OrgFormModal
          org={editOrg}
          agents={agents}
          onClose={() => { setShowCreateModal(false); setEditOrg(null); createMutation.reset(); updateMutation.reset(); }}
          onSubmit={(rawData) => {
            // `_contacts` travels alongside the form fields but is NOT part of
            // the organization payload — it is pulled out first so it never
            // reaches the backend's organization validation.
            const { _contacts, ...fields } = rawData;
            // Remove empty/null fields so backend validation doesn't choke
            const data = Object.fromEntries(
              Object.entries(fields).filter(([_, v]) => v !== '' && v !== null)
            );
            // תיקון 1 — the filter above drops empty strings, which is right for
            // optional text fields but wrong for these two: clearing them in the
            // form must CLEAR the column, not silently keep the old value. They
            // are sent as an explicit `null`, which the PATCH route understands
            // as "set to NULL" (the columns are nullable by design — `joined_at`
            // has no default and no backfill).
            if (editOrg) {
              for (const k of ['phone_secondary', 'joined_at'] as const) {
                if (fields[k] === '' || fields[k] === null) data[k] = null;
              }
              updateMutation.mutate({ id: editOrg.id, data });
            } else {
              createMutation.mutate({ ...data, _contacts: _contacts ?? [] });
            }
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
          error={editOrg ? updateMutation.error : createMutation.error}
          isAdmin={isAdmin}
          canManage={canManage}
          t={t}
        />
      )}

      {/* Exceptional hard delete (GM only): impact, reason and exact-name confirmation */}
      {deleteTarget && (
        <HardDeleteCustomerModal
          orgId={deleteTarget.id}
          orgName={deleteTarget.name}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            queryClient.invalidateQueries({ queryKey: ['organizations'] });
            setDeleteTarget(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Customer form validation (T-43): the same limits the form always enforced,
 * now on blur and on submit with a summary.
 */
const ORG_SCHEMA: FormSchema = {
  'org-name': [rules.required(), rules.minLength(2, 'validation.nameTooShort'), rules.maxLength(255, 'validation.nameTooLong')],
  'org-phone': [rules.maxLength(50, 'validation.phoneTooLong')],
  'org-phone-secondary': [rules.maxLength(50, 'validation.phoneTooLong')],
  'org-email': [rules.email()],
  'org-website': [rules.maxLength(255, 'validation.websiteTooLong'), rules.url()],
  'org-tax-id': [rules.maxLength(50, 'validation.taxIdTooLong')],
  'org-city': [rules.maxLength(100, 'validation.cityTooLong')],
};

/** P-01/A3 — the contact's business role (`contacts.contact_role`); none = null. */
const CONTACT_ROLES = ['customer_manager', 'primary_contact', 'customer_employee'] as const;

interface ContactDraft { name: string; contact_role: string; email: string; mobile: string; notes: string }
const EMPTY_CONTACT: ContactDraft = { name: '', contact_role: '', email: '', mobile: '', notes: '' };
const contactRowUsed = (c: ContactDraft) => !!(c.name.trim() || c.contact_role || c.email.trim() || c.mobile.trim() || c.notes.trim());

/**
 * Contacts staged on create: a row in use needs a name (a blank row is
 * dropped), and an email, when given, must be valid (the server rejects it
 * otherwise).
 */
function contactRowSchema(count: number): FormSchema {
  const out: FormSchema = {};
  for (let i = 0; i < count; i++) {
    out[`org-c${i}-name`] = [(v, values) => (values[`org-c${i}-used`] && !String(v ?? '').trim() ? { key: 'validation.required' } : null)];
    // QX-510 / D-135: the contact TYPE is required (no default) — a used row
    // without it would be refused by the server and silently lost.
    out[`org-c${i}-role`] = [(v, values) => (values[`org-c${i}-used`] && !String(v ?? '').trim() ? { key: 'contacts.typeRequired' } : null)];
    out[`org-c${i}-email`] = [rules.email()];
  }
  return out;
}

/** P-01/A2 — a new customer joins today (local date), editable before saving. */
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** One group of the customer form under a quiet title. */
function FormGroup({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <fieldset className="space-y-3">
      {title && <legend className="mb-3 text-sm font-semibold text-text">{title}</legend>}
      {children}
    </fieldset>
  );
}

/**
 * Canonical Create/Edit Customer form.
 *
 * Exported so the Customer Hub (Repair 5) reuses this exact form for
 * "Edit Customer" instead of growing a second one. `isAdmin` only drives the
 * account-manager picker; the caller keeps the payload shaping. `error` is the
 * rejected save, shown in the form in Hebrew.
 */
export function OrgFormModal({ org, agents, onClose, onSubmit, isLoading, isAdmin, canManage, t, error }: {
  org: Organization | null;
  agents: any[];
  onClose: () => void;
  onSubmit: (data: any) => void;
  isLoading: boolean;
  isAdmin: boolean;
  canManage: boolean;
  t: any;
  error?: unknown;
}) {
  // תיקון 1 — a Customer carries NO billing terms. `billing_model`,
  // `hourly_rate` and `retainer_hours` are deliberately absent from this form:
  // they used to default to 'hourly' / 0 / 0, which meant every new Customer
  // was silently written with an implied hourly model it never agreed to.
  // Billing terms belong to the Engagement, so they are not collected here at
  // all — the legacy columns stay in the DB for safe transition only.
  const [form, setForm] = useState({
    name: org?.name || '',
    account_manager_id: org?.account_manager_id || '',
    tax_id: org?.tax_id || '',
    email: org?.email || '',
    phone: org?.phone || '',
    phone_secondary: org?.phone_secondary || '',
    // P-01/A2 — create: today by default; edit: the stored value.
    joined_at: org?.joined_at ? String(org.joined_at).slice(0, 10) : (org ? '' : todayIso()),
    address: org?.address || '',
    city: org?.city || '',
    website: org?.website || '',
    notes: org?.notes || '',
    active: org?.active ?? true,
    is_demo: org?.is_demo === true, // D-352
  });
  const set = (field: keyof typeof form, value: any) => setForm((prev) => ({ ...prev, [field]: value }));

  /**
   * Contacts staged for a customer that does not exist yet. They are sent as a
   * sibling of the organization payload and created by the CALLER after the
   * organization insert, because a contact needs the organization's id — which
   * only exists once that insert returns. Keeping them out of `form` avoids
   * leaking a contacts array into the organization update path, where there is
   * no such field.
   */
  const [contacts, setContacts] = useState<ContactDraft[]>([]);

  // D-138 / D-141 — "יצירת גישת פורטל" on a NEW customer (the GM's): the
  // e-mail follows the customer's e-mail until edited.
  // D-147 (QX-79): also the employee who is the new customer's Account Manager.
  const currentUserId = useAuthStore((s) => s.user?.id);
  // D-331 Q3 (round 2): a TL / PM opens a customer with no Account Manager - the OTE manager appoints one
  // (the server refuses it too). The OTE manager and an Account Manager name one as before.
  const perms = usePermissions();
  const namesAccountManager = !!org || isAdmin || perms.source !== 'server'
    || perms.capabilities.isGeneralManager; // round 3: an Account Manager of another customer no longer names one on a new customer
  const showManagerPicker = canManage && namesAccountManager;
  const offerPortalAccess = !org && (isAdmin || (!!currentUserId && form.account_manager_id === currentUserId));
  const [portalOn, setPortalOn] = useState(false);
  const [portalEmail, setPortalEmail] = useState('');
  const [portalPassword, setPortalPassword] = useState('');
  const prevOrgEmail = useRef('');
  useEffect(() => {
    // DQ-122: read the ref now, not inside the updater (it runs after the ref is overwritten).
    const before = prevOrgEmail.current;
    setPortalEmail((cur) => followEmail(before, form.email, cur));
    prevOrgEmail.current = form.email;
  }, [form.email]);
  const portalBlock = offerPortalAccess && portalOn
    ? accessBlocker({ email: portalEmail, password: portalPassword, needsLevel: false })
    : undefined;

  const addContactRow = () =>
    setContacts((prev) => [...prev, { ...EMPTY_CONTACT }]);

  const updateContactRow = (i: number, field: string, value: string) =>
    setContacts((prev) => prev.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)));

  const removeContactRow = (i: number) =>
    setContacts((prev) => prev.filter((_, idx) => idx !== i));

  // P-01/A1 — the Account Manager is mandatory (create and edit) wherever the
  // picker is shown; only active OTE employees are offered.
  // FIX-B17 (D-198 QX-152) — never a "פנימי בלבד" employee on a customer (the
  // company itself is open; a current value stays visible). The server refuses it too.
  const managerOptions = agents.filter((agent: any) => agent.active !== false
    && (agent.internal_only !== true || org?.is_internal === true || agent.id === org?.account_manager_id));
  const schema = useMemo(() => ({
    ...(showManagerPicker && org ? { 'org-account-manager-id': [rules.required()] } : {}),
    ...ORG_SCHEMA,
    ...(org ? {} : contactRowSchema(contacts.length)),
  }), [org, contacts.length, showManagerPicker]);
  const values: Record<string, unknown> = {
    'org-account-manager-id': form.account_manager_id,
    'org-name': form.name,
    'org-phone': form.phone,
    'org-phone-secondary': form.phone_secondary,
    'org-email': form.email,
    'org-website': form.website,
    'org-tax-id': form.tax_id,
    'org-city': form.city,
  };
  contacts.forEach((c, i) => {
    values[`org-c${i}-name`] = c.name;
    values[`org-c${i}-used`] = contactRowUsed(c);
    values[`org-c${i}-role`] = c.contact_role;
    values[`org-c${i}-email`] = c.email;
  });
  const v = useFormValidation(schema, values);
  const fieldLabels: Record<string, string> = {
    'org-account-manager-id': t('admin.accountManager'),
    'org-name': t('admin.orgName'),
    'org-phone': t('admin.orgPhone'),
    'org-phone-secondary': t('admin.orgPhoneSecondary'),
    'org-email': t('admin.orgEmail'),
    'org-website': t('admin.orgWebsite'),
    'org-tax-id': t('admin.taxId'),
    'org-city': t('admin.orgCity'),
  };
  contacts.forEach((_, i) => {
    fieldLabels[`org-c${i}-name`] = `${t('contacts.name')} (${t('form.contactN', { n: i + 1 })})`;
    fieldLabels[`org-c${i}-role`] = `${t('contacts.role')} (${t('form.contactN', { n: i + 1 })})`;
    fieldLabels[`org-c${i}-email`] = `${t('contacts.email')} (${t('form.contactN', { n: i + 1 })})`;
  });

  const handleSubmit = v.submit(() => {
    if (portalBlock) return;
    // Rows left entirely blank are dropped rather than rejected — an empty
    // added row should not block saving the customer. P-01/A3: role
    // (`contact_role`, null when not chosen), mobile (`contacts.mobile`) and
    // notes travel with each contact; no portal access is created here.
    const filled = contacts.filter(contactRowUsed).map((c) => ({
      name: c.name.trim(),
      contact_role: c.contact_role || null,
      email: c.email.trim() || undefined,
      mobile: c.mobile.trim() || undefined,
      notes: c.notes.trim() || undefined,
    }));
    // The Customer payload is an explicit allow-list of canonical Customer
    // fields. Spreading the form state would put whatever a future field
    // happens to be onto the wire; naming the fields here means a legacy
    // billing or Engagement field cannot travel just by existing in state.
    const payload = {
      name: form.name,
      account_manager_id: form.account_manager_id,
      tax_id: form.tax_id,
      email: form.email,
      phone: form.phone,
      phone_secondary: form.phone_secondary,
      joined_at: form.joined_at,
      address: form.address,
      city: form.city,
      website: form.website,
      notes: form.notes,
      active: form.active,
      // D-352: "לקוח הדגמה" - the OTE manager only (the server refuses anyone else), so it is sent only when it matters.
      ...(perms.capabilities.isGeneralManager && (form.is_demo || org?.is_demo === true) ? { is_demo: form.is_demo } : {}),
    };
    // `_contacts` is a SIBLING of the payload, never a member of it, and it is
    // only meaningful when the customer is being created — a contact row is
    // created against an id that does not exist yet on an edit, and the edit
    // path has no use for it. Sending it unconditionally put a stray key on
    // the update payload, so it is added only for a create.
    onSubmit(org ? payload : {
      ...payload,
      _contacts: filled,
      ...customerPortalAccessPayload({ enabled: offerPortalAccess && portalOn, email: portalEmail, password: portalPassword }),
    });
  });

  const formId = 'org-form';
  return (
    <Dialog
      open
      onClose={onClose}
      title={org ? t('admin.editOrg') : t('admin.createOrg')}
      size="lg"
      dismissible={!isLoading}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary flex-1 sm:flex-none">{t('common.cancel')}</button>
          <button type="submit" form={formId} disabled={isLoading} aria-busy={isLoading || undefined} className="btn btn-primary flex-1 sm:flex-none">
            {isLoading && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {org ? t('form.save') : t('admin.createOrg')}
          </button>
        </>
      )}
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-6">
        {v.showSummary && <FormErrorSummary errors={v.errors} labels={fieldLabels} order={Object.keys(schema)} />}

        <FormGroup>
          <Field id="org-name" label={t('admin.orgName')} required error={v.errors['org-name']}>
            <input type="text" value={form.name} onChange={e => set('name', e.target.value)} onBlur={() => v.blur('org-name')}
              className="input" maxLength={255} autoComplete="organization" />
          </Field>
          {canManage && !namesAccountManager && (
            <p className="text-xs text-text-2">{t('admin.accountManagerByOteManager')}</p>
          )}
          {showManagerPicker && (
            <Field id="org-account-manager-id" label={t('admin.accountManager')} required={!!org} error={v.errors['org-account-manager-id']}>
              <EntityPicker
                placeholder={t('admin.selectAccountManager')}
                options={managerOptions.map((agent: any) => ({ value: agent.id, label: agent.name }))}
                value={form.account_manager_id}
                onChange={(id) => set('account_manager_id', id)}
                onBlur={() => v.blur('org-account-manager-id')}
              />
            </Field>
          )}
        </FormGroup>

        <FormGroup title={t('form.sectionContact')}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field id="org-phone" label={t('admin.orgPhone')} error={v.errors['org-phone']}>
              <input type="tel" inputMode="tel" dir="ltr" autoComplete="tel" value={form.phone} onChange={e => set('phone', e.target.value)}
                onBlur={() => v.blur('org-phone')} className="input text-start" maxLength={50} />
            </Field>
            <Field id="org-phone-secondary" label={t('admin.orgPhoneSecondary')} error={v.errors['org-phone-secondary']}>
              <input type="tel" inputMode="tel" dir="ltr" autoComplete="tel" value={form.phone_secondary} onChange={e => set('phone_secondary', e.target.value)}
                onBlur={() => v.blur('org-phone-secondary')} className="input text-start" maxLength={50} />
            </Field>
            <Field id="org-email" label={t('admin.orgEmail')} error={v.errors['org-email']}>
              <input type="email" dir="ltr" spellCheck={false} autoComplete="email" value={form.email} onChange={e => set('email', e.target.value)}
                onBlur={() => v.blur('org-email')} className="input text-start" />
            </Field>
            <Field id="org-website" label={t('admin.orgWebsite')} error={v.errors['org-website']}>
              <input type="text" inputMode="url" dir="ltr" spellCheck={false} autoComplete="url" value={form.website} onChange={e => set('website', e.target.value)}
                onBlur={() => v.blur('org-website')} className="input text-start" maxLength={255} />
            </Field>
          </div>
        </FormGroup>

        <FormGroup title={t('form.sectionDetails')}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field id="org-tax-id" label={t('admin.taxId')} error={v.errors['org-tax-id']}>
              <input type="text" inputMode="numeric" dir="ltr" value={form.tax_id} onChange={e => set('tax_id', e.target.value)}
                onBlur={() => v.blur('org-tax-id')} className="input text-start" maxLength={50} />
            </Field>
            <Field id="org-joined-at" label={t('admin.orgJoinedAt')}>
              <DateInput dir="ltr" value={form.joined_at} onChange={e => set('joined_at', e.target.value)} className="input text-start" />
            </Field>
            <Field id="org-city" label={t('admin.orgCity')} error={v.errors['org-city']}>
              <input type="text" value={form.city} onChange={e => set('city', e.target.value)}
                onBlur={() => v.blur('org-city')} className="input" maxLength={100} autoComplete="address-level2" />
            </Field>
            <Field id="org-address" label={t('admin.orgAddress')}>
              <input type="text" value={form.address} onChange={e => set('address', e.target.value)} className="input" autoComplete="street-address" />
            </Field>
          </div>
          <Field id="org-notes" label={t('admin.orgNotes')}>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} className="input min-h-20 resize-none" rows={3} />
          </Field>
          {org && perms.capabilities.isGeneralManager && org.merged_into_internal_at && (
            <p className="text-sm text-surface-600">{t('admin.orgMergedInternal')}</p>
          )}
          {org && perms.capabilities.isGeneralManager && !org.merged_into_internal_at && (
            <div>
              <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-text">
                <input type="checkbox" checked={form.is_demo} onChange={e => set('is_demo', e.target.checked)} className="h-4 w-4 rounded-xs accent-primary-700" aria-describedby="org-demo-hint" />
                {t('admin.orgDemo')}
              </label>
              <p id="org-demo-hint" className="text-xs text-surface-600">{t('admin.orgDemoHint')}</p>
            </div>
          )}
          {isAdmin && org && (
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-text">
              <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} className="h-4 w-4 rounded-xs accent-primary-700" />
              {form.active ? t('contacts.active') : t('contacts.inactive')}
            </label>
          )}
        </FormGroup>

        {offerPortalAccess && (
          <CustomerPortalAccessFields
            idPrefix="org-portal"
            enabled={portalOn}
            onEnabled={setPortalOn}
            email={portalEmail}
            onEmail={setPortalEmail}
            password={portalPassword}
            onPassword={setPortalPassword}
            error={portalBlock ? t(portalBlock) : undefined}
          />
        )}

        {/* Contacts of the customer being created. Only offered on CREATE:
            on edit, contacts are managed in the Contacts screen, which owns
            their full record (including the portal grant). */}
        {!org && namesAccountManager && (
          <section aria-labelledby="org-contacts-title" className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h3 id="org-contacts-title" className="text-sm font-semibold text-text">{t('admin.contacts')}</h3>
              <button type="button" onClick={addContactRow} className="btn btn-ghost btn-sm text-accent-text">
                <Plus size={14} aria-hidden="true" /> {t('contacts.addContact')}
              </button>
            </div>

            {contacts.length === 0 ? (
              <p className="text-xs text-text-2">{t('contacts.empty')}</p>
            ) : (
              <ul className="space-y-3">
                {contacts.map((c, i) => (
                  <li key={i} className="rounded-lg bg-surface-subtle p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-xs font-medium text-text-2">{t('form.contactN', { n: i + 1 })}</p>
                      <button type="button" onClick={() => removeContactRow(i)}
                        className="btn-icon touch-target text-text-3 hover:bg-danger-50 hover:text-danger-700"
                        aria-label={`${t('common.remove')}: ${c.name || t('form.contactN', { n: i + 1 })}`}>
                        <Trash2 size={16} strokeWidth={1.75} aria-hidden="true" />
                      </button>
                    </div>
                    {/* P-01/A3 — name* | role | email | mobile | notes */}
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Field id={`org-c${i}-name`} label={t('contacts.name')} required error={v.errors[`org-c${i}-name`]}>
                        <input type="text" value={c.name} onChange={e => updateContactRow(i, 'name', e.target.value)}
                          onBlur={() => v.blur(`org-c${i}-name`)} className="input" maxLength={255} />
                      </Field>
                      <Field id={`org-c${i}-role`} label={t('contacts.role')} required error={v.errors[`org-c${i}-role`]}>
                        <select value={c.contact_role} onChange={e => updateContactRow(i, 'contact_role', e.target.value)}
                          onBlur={() => v.blur(`org-c${i}-role`)} className="input">
                          <option value="">{t('contacts.typeRequired')}</option>
                          {CONTACT_ROLES.map((r) => <option key={r} value={r}>{t(`contacts.contactRole.${r}`)}</option>)}
                        </select>
                      </Field>
                      <Field id={`org-c${i}-email`} label={t('contacts.email')} error={v.errors[`org-c${i}-email`]}>
                        <input type="email" dir="ltr" spellCheck={false} autoComplete="off" value={c.email}
                          onChange={e => updateContactRow(i, 'email', e.target.value)} onBlur={() => v.blur(`org-c${i}-email`)} className="input text-start" />
                      </Field>
                      <Field id={`org-c${i}-mobile`} label={t('contacts.mobile')}>
                        <input type="tel" inputMode="tel" dir="ltr" autoComplete="off" value={c.mobile}
                          onChange={e => updateContactRow(i, 'mobile', e.target.value)} className="input text-start" maxLength={50} />
                      </Field>
                      <Field id={`org-c${i}-notes`} label={t('contacts.notes')} className="sm:col-span-2">
                        <textarea value={c.notes} onChange={e => updateContactRow(i, 'notes', e.target.value)} className="input min-h-16 resize-none" rows={2} />
                      </Field>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <FormServerError error={error} fallback={t('common.saveError')} />
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// P-06/G — the list's state in the URL (q, status, am, from, to, et, ep, sc,
// hb, sort, dir, page). Defaults are left out, so a plain /organizations stays plain.
// ---------------------------------------------------------------------------
interface OrgListUrl {
  search: string;
  status: 'all' | 'active' | 'inactive';
  accountManagerId: string;
  joinedFrom: string;
  joinedTo: string;
  engagementFilter: EngagementFilterValue;
  sort: SortKey;
  dir: 'asc' | 'desc';
  page: number;
}

const ENGAGEMENT_TYPES = ['project', 'development', 'service_contract', 'hour_bank', 'service_without_contract'];

function readOrgListUrl(sp: URLSearchParams): OrgListUrl {
  const st = sp.get('status');
  const sortKey = sp.get('sort');
  const page = Number(sp.get('page'));
  return {
    search: sp.get('q') ?? '',
    status: st === 'all' || st === 'inactive' ? st : 'active',
    accountManagerId: sp.get('am') ?? '',
    joinedFrom: sp.get('from') ?? '',
    joinedTo: sp.get('to') ?? '',
    engagementFilter: {
      // D-264: old links with ?ep / ?sc / ?hb become the matching type + "active only".
      ...foldLegacyEngagementFlags(
        (sp.get('et') ?? '').split(',').filter((x) => ENGAGEMENT_TYPES.includes(x)),
        { activeProject: sp.get('ep') === '1', serviceContract: sp.get('sc') === '1', hourBank: sp.get('hb') === '1' },
        sp.get('ao') === '1',
      ) as EngagementFilterValue,
    },
    sort: (sortKey || 'name') as SortKey,
    dir: sp.get('dir') === 'desc' ? 'desc' : 'asc',
    page: Number.isInteger(page) && page > 0 ? page - 1 : 0,
  };
}

function writeOrgListUrl(prev: URLSearchParams, v: OrgListUrl): URLSearchParams {
  const next = new URLSearchParams(prev);
  const put = (k: string, val: string, def = '') => { if (val && val !== def) next.set(k, val); else next.delete(k); };
  put('q', v.search);
  put('status', v.status, 'active');
  put('am', v.accountManagerId);
  put('from', v.joinedFrom);
  put('to', v.joinedTo);
  put('et', v.engagementFilter.types.join(','));
  put('ao', v.engagementFilter.activeOnly ? '1' : '');
  put('ep', '');
  put('sc', '');
  put('hb', '');
  put('sort', v.sort, 'name');
  put('dir', v.dir, 'asc');
  put('page', v.page > 0 ? String(v.page + 1) : '');
  return next;
}
